import { LOCAL_PLAYER_NUMBERS, PlayerNumber } from '~/modules/players/player-number';
import { serverRpc } from '~/modules/remote-mic/network/client';
import { useSubscription } from '~/modules/remote-mic/network/client/hooks/use-subscription';
import ColourTiles from '~/routes/remote-mic/components/colour-tiles';
import { usePhoneToast } from '~/routes/remote-mic/components/phone-toast-context';
import { playerColourName } from '~/routes/remote-mic/components/player-colour';

interface Props {
  id: string;
  name: string;
  playerNumber: PlayerNumber | null;
  onModalClose: () => void;
}

// Moving another phone between colours from Manage the game, or taking it out of the game.
export default function ManagePlayerPanel({ playerNumber, id, name, onModalClose }: Props) {
  const list = useSubscription('remote-mics') ?? [];
  const flash = usePhoneToast();

  const selectPlayer = (player: PlayerNumber | null) => {
    serverRpc.players.requestMicSelect(id, player).catch(() => flash('The TV did not move the phone'));
    onModalClose();
  };

  const joined = playerNumber !== null;

  const occupants: Partial<Record<PlayerNumber, string>> = {};
  for (const mic of list) {
    if (mic.number !== null && mic.id !== id) occupants[mic.number] ??= mic.name;
  }

  return (
    <div className="flex flex-col gap-3 pb-2">
      <ColourTiles
        playerNumbers={LOCAL_PLAYER_NUMBERS}
        selected={playerNumber}
        ownName={name}
        occupants={occupants}
        onPick={selectPlayer}
        onBlocked={(number, occupant) => flash(`${occupant} has ${playerColourName(number)}`)}
        allowOccupied
      />
      <div className="flex gap-2.5">
        <button
          type="button"
          onClick={() => selectPlayer(null)}
          disabled={!joined}
          data-test="change-to-unset"
          className="flex h-16 flex-1 cursor-pointer items-center justify-center rounded-[1.125rem] border-none bg-white/8 text-[1.0625rem] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-transform duration-100 active:scale-[0.98] disabled:opacity-40">
          Unassign
        </button>
        <button
          type="button"
          onClick={() => void serverRpc.players.removePlayer(id)}
          data-test="remove-player"
          className="flex h-16 flex-1 cursor-pointer items-center justify-center rounded-[1.125rem] border-none bg-white/8 text-[1.0625rem] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-transform duration-100 active:scale-[0.98]">
          Remove
        </button>
        <button
          type="button"
          onClick={onModalClose}
          data-test="close-menu"
          className="bg-active flex h-16 flex-1 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.1875rem] font-bold text-black transition-transform duration-100 active:scale-[0.98]">
          Done
        </button>
      </div>
    </div>
  );
}
